import {
  ProductTypeEnum,
  StoreImage,
  StoreItemEnum,
  ThumbnailBaseCommonType,
} from '@systemeio/digital-store-shared'
import { ActionMenu, ActionMenuItem, toast } from '@systemeio/ui'
import { ItemDeleteModal } from 'modules/stores/store/components/item-delete-modal'
import { STORE_ITEM_CLASS_NAME } from 'modules/stores/store/constants/store-item-class-name'
import {
  storeItemProductTitleLocoKeys,
  storeItemTitleLocoKeys,
} from 'modules/stores/store/constants/store-item-constants'
import { useStoreQuery } from 'modules/stores/store/hooks/use-store-query'
import { StoreItemWithTabType } from 'modules/stores/store/types/store-item-interface'
import { getImageTypeFromItem } from 'modules/stores/store/utils/get-image-type-from-item'
import { getTabFromItem } from 'modules/stores/store/utils/get-tab-from-item'
import React, { FC } from 'react'
import Confirmation from 'shared/components/confirmation-modal'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useModal } from 'shared/hooks/use-modal'
import { handle422Error } from 'shared/utils/parse-422-errors'
import { twJoin } from 'tailwind-merge'
import { StoreInterface } from '../../types/stores-interface'
import { useStoreEditItemContext } from '../context/store-item-edit-context'
import { useStore } from '../hooks/use-store'

type Props =
  | {
      itemType: Exclude<StoreItemWithTabType, StoreItemEnum.product>
      thumbnail: ThumbnailBaseCommonType['thumbnail']
      productType?: never
      title: string
      itemId: number
      onDelete: () => Promise<void>
      onSaveDraft: () => Promise<void>
      onPublish: () => Promise<void>
      draft: boolean
    }
  | {
      itemType: Extract<StoreItemWithTabType, StoreItemEnum.product>
      productType: ProductTypeEnum
      thumbnail: ThumbnailBaseCommonType['thumbnail']
      title: string
      itemId: number
      onDelete: () => Promise<void>
      onSaveDraft: () => Promise<void>
      onPublish: () => Promise<void>
      draft: boolean
    }

function isProductItem(props: Props): props is Extract<Props, { itemType: StoreItemEnum.product }> {
  return props.itemType === StoreItemEnum.product
}

const ItemContent: FC<Props> = props => {
  const { thumbnail, draft, title, itemId, onDelete, onSaveDraft, onPublish } = props

  const { setErrors } = useStoreEditItemContext()

  const { t } = useLocoTranslation()

  const { onEditItem, storeId } = useStoreQuery()
  const { mutate } = useStore(storeId)

  const saveDraftModal = useModal()
  const publishModal = useModal()
  const deleteModal = useModal()

  const openEditItem = async () => {
    await onEditItem(itemId, getTabFromItem(props.itemType), props.productType)
  }

  const onItemClick = async (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const target = e.target as HTMLDivElement
    const button = target.closest('button')
    const link = target.closest('a')
    const modalBackground = target.closest('#modal-background')
    if (button || link || modalBackground) return
    await openEditItem()
  }

  const imageType = isProductItem(props)
    ? getImageTypeFromItem({ itemType: props.itemType, productType: props.productType })
    : getImageTypeFromItem({ itemType: props.itemType })

  const itemTitle = isProductItem(props)
    ? storeItemProductTitleLocoKeys[props.productType]
    : storeItemTitleLocoKeys[props.itemType]

  const onMutateDraftStatus = async (draft: boolean) => {
    await mutate(data => {
      if (!data) return undefined

      const newData: StoreInterface = { ...data }

      newData.items = data.items.map(item => {
        if (item.itemId === itemId) {
          return { ...item, draft }
        }

        return item
      })

      return newData
    })
  }

  const onSaveDraftConfirm = async () => {
    try {
      setErrors({})
      await onSaveDraft()
      await onMutateDraftStatus(true)
    } catch (e) {
      handle422Error(e, setErrors)
      toast.error(t('global.error'))
      await openEditItem()
      throw e
    }
  }

  const onPublishConfirm = async () => {
    try {
      setErrors({})
      await onPublish()
      await onMutateDraftStatus(false)
    } catch (e) {
      handle422Error(e, setErrors)
      toast.error(t('global.error'))
      await openEditItem()
      throw e
    }
  }

  return (
    <div
      className={twJoin(
        STORE_ITEM_CLASS_NAME,
        'flex w-full cursor-pointer items-center gap-3 border border-l-4 border-transparent sm:gap-5',
        draft ? 'border-l-brand-primary' : 'border-l-text-primary',
      )}
      onClick={onItemClick}
    >
      <StoreImage
        imageType={imageType}
        thumbnail={thumbnail}
        skipTheme
        alt={title}
        className={'w-12 min-w-12 rounded-xl sm:w-16 sm:min-w-16'}
      />
      <div className={'flex flex-col items-start gap-1'}>
        <span className={'text-brand-primary'}>{t(itemTitle)}</span>
        <b
          className={'text-text-primary text-[16px] sm:text-[18px]'}
          style={{
            wordBreak: 'break-word',
          }}
        >
          {title}
        </b>
      </div>
      <div className={'ml-auto flex items-center gap-3'}>
        {draft && (
          <span className={'bg-background-primary text-brand-primary rounded-lg p-2'}>
            {t('dashboard.store.item.draft')}
          </span>
        )}
        <ActionMenu
          menuItems={[
            !draft ? (
              <ActionMenuItem
                key={'action-save-as-draft'}
                onClick={() => {
                  saveDraftModal.open()
                }}
                label={t('dashboard.store.item.save_draft')}
              />
            ) : undefined,
            draft ? (
              <ActionMenuItem
                key={'action-publish'}
                onClick={() => {
                  publishModal.open()
                }}
                label={t('dashboard.store.item.publish')}
              />
            ) : undefined,
            <ActionMenuItem
              key={'action-delete'}
              onClick={() => {
                deleteModal.open()
              }}
              label={t('global.delete')}
            />,
          ].filter(e => !!e)}
        />
        <Confirmation
          opened={saveDraftModal.opened}
          onClose={saveDraftModal.close}
          onConfirm={onSaveDraftConfirm}
          confirmationText={t('dashboard.store.item.save_draft_confirmation')}
          toastCaption={t('dashboard.store.item.save_draft_success')}
        />
        <Confirmation
          opened={publishModal.opened}
          onClose={publishModal.close}
          onConfirm={onPublishConfirm}
          confirmationText={t('dashboard.store.item.publish_confirmation')}
          toastCaption={t('dashboard.store.item.publish_success')}
        />
        <ItemDeleteModal
          onDelete={onDelete}
          opened={deleteModal.opened}
          onClose={deleteModal.close}
        />
      </div>
    </div>
  )
}

export default ItemContent
